import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  getPublicReferenceForm,
  submitPublicReferenceForm,
} from "../../redux/actions/reference-check-actions";

const RatingRow = ({ label, value, onChange }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
    <span style={{ fontSize: 14, color: "#334155" }}>{label}</span>
    <div style={{ display: "flex", gap: 6 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          style={{
            width: 36, height: 36, borderRadius: 8,
            border: value === n ? "2px solid #7B5AC4" : "1px solid #E2E8F0",
            background: value === n ? "#EEF2FF" : "white",
            color: value === n ? "#4338CA" : "#64748B",
            fontWeight: 600, cursor: "pointer",
            transition: "all 0.15s",
          }}
        >
          {n}
        </button>
      ))}
    </div>
  </div>
);

const PublicReferenceForm = () => {
  const { formToken } = useParams();
  const dispatch = useDispatch();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [ratings, setRatings] = useState({
    ratingOverall: 0,
    ratingSkills: 0,
    ratingReliability: 0,
    ratingCommunication: 0,
    ratingCultureFit: 0,
  });
  const [wouldRehire, setWouldRehire] = useState(null);
  const [comments, setComments] = useState("");

  useEffect(() => {
    document.title = "Reference form · Ample Hire";
    (async () => {
      try {
        const res = await dispatch(getPublicReferenceForm({ token: formToken }));
        if (res.error) {
          setError(res.payload || "Form not found");
        } else {
          setMeta(res.payload);
          if (res.payload?.status === "COMPLETED") setSubmitted(true);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [dispatch, formToken]);

  const allRated = Object.values(ratings).every((v) => v > 0);

  const onSubmit = async () => {
    if (!allRated || wouldRehire === null) return;
    setSubmitting(true);
    try {
      const res = await dispatch(submitPublicReferenceForm({
        token: formToken,
        payload: { ...ratings, wouldRehire, comments },
      }));
      if (res.error) {
        setError(res.payload || "Submission failed");
      } else {
        setSubmitted(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: "#F8FAFC", padding: "32px 16px",
      fontFamily: "system-ui, -apple-system, sans-serif",
    }}>
      <div style={{
        maxWidth: 640, margin: "0 auto", background: "white",
        borderRadius: 16, border: "1px solid #E2E8F0", padding: 32,
        boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, background: "#7B5AC4",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "white", fontWeight: 800, fontSize: 14,
          }}>A</div>
          <div style={{ fontSize: 13, color: "#94A3B8", fontWeight: 500 }}>Ample Hire</div>
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: "16px 0 8px" }}>
          Reference Check
        </h1>

        {loading && <div style={{ color: "#64748B" }}>Loading form…</div>}

        {error && (
          <div style={{
            padding: 16, background: "#FEF2F2", border: "1px solid #FCA5A5",
            borderRadius: 8, color: "#991B1B", fontSize: 14,
          }}>
            {error}
          </div>
        )}

        {!loading && meta && submitted && (
          <div style={{ textAlign: "center", padding: "32px 16px" }}>
            <div style={{
              width: 64, height: 64, borderRadius: 32, background: "#10B981",
              margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center",
              color: "white", fontSize: 32,
            }}>✓</div>
            <h2 style={{ fontSize: 18, color: "#0F172A", marginBottom: 8 }}>Thank you</h2>
            <p style={{ color: "#64748B", fontSize: 14 }}>
              Your reference has been submitted to the hiring team. They'll see your feedback in the candidate report.
            </p>
            {meta.aiSentimentScore != null && (
              <div style={{ marginTop: 16, fontSize: 12, color: "#94A3B8" }}>
                AI sentiment score: <strong>{Math.round(Number(meta.aiSentimentScore))}/100</strong> · {meta.aiSentimentLabel?.replace("_", " ").toLowerCase()}
              </div>
            )}
          </div>
        )}

        {!loading && meta && !submitted && (
          <>
            <p style={{ color: "#64748B", fontSize: 14, marginBottom: 24 }}>
              You've been invited to provide a reference. Your responses will be reviewed by the hiring team alongside other candidate signals.
            </p>

            <div style={{
              padding: 12, background: "#F1F5F9", borderRadius: 8, marginBottom: 24, fontSize: 12,
            }}>
              <div style={{ color: "#94A3B8", textTransform: "uppercase", fontSize: 10, letterSpacing: 0.5, marginBottom: 4 }}>
                Sent to
              </div>
              <div style={{ color: "#334155", fontWeight: 500 }}>
                {meta.refereeName || meta.refereeEmail}
                {meta.refereeRelationship && <span style={{ color: "#94A3B8" }}> · {meta.refereeRelationship}</span>}
              </div>
            </div>

            <h3 style={{ fontSize: 14, fontWeight: 600, color: "#0F172A", marginBottom: 16 }}>
              Rate the candidate (1 = Poor, 5 = Excellent)
            </h3>

            {[
              ["Overall impression", "ratingOverall"],
              ["Job-relevant skills", "ratingSkills"],
              ["Reliability & ownership", "ratingReliability"],
              ["Communication", "ratingCommunication"],
              ["Culture & team fit", "ratingCultureFit"],
            ].map(([lbl, key]) => (
              <RatingRow
                key={key}
                label={lbl}
                value={ratings[key]}
                onChange={(v) => setRatings((r) => ({ ...r, [key]: v }))}
              />
            ))}

            <div style={{ margin: "20px 0", padding: 12, background: "#FAFAFA", borderRadius: 8 }}>
              <div style={{ fontSize: 13, color: "#0F172A", fontWeight: 500, marginBottom: 8 }}>
                Would you rehire / work with this person again?
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setWouldRehire(true)}
                  style={{
                    flex: 1, padding: "8px 12px", borderRadius: 6,
                    border: wouldRehire === true ? "2px solid #10B981" : "1px solid #E2E8F0",
                    background: wouldRehire === true ? "#ECFDF5" : "white",
                    color: wouldRehire === true ? "#047857" : "#64748B",
                    fontWeight: 600, cursor: "pointer",
                  }}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setWouldRehire(false)}
                  style={{
                    flex: 1, padding: "8px 12px", borderRadius: 6,
                    border: wouldRehire === false ? "2px solid #EF4444" : "1px solid #E2E8F0",
                    background: wouldRehire === false ? "#FEF2F2" : "white",
                    color: wouldRehire === false ? "#991B1B" : "#64748B",
                    fontWeight: 600, cursor: "pointer",
                  }}
                >
                  No
                </button>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 13, color: "#0F172A", fontWeight: 500, marginBottom: 6 }}>
                Comments (optional)
              </label>
              <textarea
                rows={4}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Share specific examples or context that would help the hiring team…"
                style={{
                  width: "100%", padding: 10, borderRadius: 6,
                  border: "1px solid #E2E8F0", fontSize: 13, color: "#0F172A",
                  fontFamily: "inherit", resize: "vertical",
                }}
              />
            </div>

            <button
              type="button"
              onClick={onSubmit}
              disabled={!allRated || wouldRehire === null || submitting}
              style={{
                width: "100%", padding: "12px 16px", borderRadius: 8,
                background: !allRated || wouldRehire === null ? "#CBD5E1" : "#7B5AC4",
                color: "white", fontWeight: 600, fontSize: 14,
                border: "none", cursor: (!allRated || wouldRehire === null) ? "not-allowed" : "pointer",
              }}
            >
              {submitting ? "Submitting…" : "Submit reference"}
            </button>
            <div style={{ marginTop: 8, fontSize: 11, color: "#94A3B8", textAlign: "center" }}>
              By submitting you confirm your responses are honest and based on direct experience.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PublicReferenceForm;
